import React, { useEffect, useState } from "react";
import { stripRichMarkers } from "@/lib/richFormat";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Star } from "lucide-react";
import { api } from "@/lib/api";
import { whenFeedReady } from "@/lib/lowPriority";
import { useApp } from "@/context/AppContext";
import { avatarSrc } from "@/lib/defaults";
import { excerpt } from "@/lib/prayers";
import { imageVariant } from "@/lib/mediaUrl";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DismissX } from "@/components/smart/DismissX";

const GREEN = "var(--yalola-secondary)";
const GREEN_SOFT = "var(--yalola-secondary-soft)";
const GREEN_LINE = "color-mix(in srgb, var(--yalola-secondary) 40%, transparent)";
const CATS = { healing: ["testimony.cat_healing", "Guérison"], provision: ["testimony.cat_provision", "Provision"], salvation: ["testimony.cat_salvation", "Salut"], family: ["testimony.cat_family", "Famille"], other: ["testimony.cat_other", "Autre"] };

export const featuredChars = (config) => {
  const n = Number(config?.settings?.testimony_featured_chars);
  return n >= 60 && n <= 400 ? n : 160;
};

// Données (exportées pour les cartes intelligentes) ; `enabled=false` → aucune requête.
export const useFeaturedTestimony = (enabled = true) => {
  const [post, setPost] = useState(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!enabled) { setPost(null); return undefined; }
    let alive = true;
    const cancel = whenFeedReady(() => {
      api.get("/testimonies/featured")
        .then(({ data }) => { if (alive) setPost(data.testimony || null); })
        .catch(() => {})
        .finally(() => { if (alive) setLoaded(true); });
    });
    return () => { alive = false; cancel(); };
  }, [enabled]);
  return { post, loaded };
};

// « Témoignage de la semaine » — compact green card (like the prayer chain banner): author, short excerpt,
// optional square thumbnail, tap anywhere → the full post at /post/{id}. Never the whole publication.
// `authorId` (profile page): only shown when the featured testimony belongs to that member.
// `collapsed` (profil, cartes intelligentes) : titre + 1 ligne + « Lire », dépliée au tap.
export const FeaturedTestimony = ({ authorId = null, data = undefined, onDismiss = null, collapsed = false }) => {
  const { t, config } = useApp();
  const nav = useNavigate();
  const enabled = config?.settings?.features?.testimonies !== false;
  const own = useFeaturedTestimony(enabled && data === undefined);
  const post = data === undefined ? own.post : data;
  const [expanded, setExpanded] = useState(false);

  if (!enabled || !post) return null;
  if (authorId && post.author?.id !== authorId) return null;
  const cat = CATS[post.testimony_category] || CATS.other;
  const text = excerpt(stripRichMarkers(post.content), featuredChars(config));
  const image = (post.images || [])[0];
  const folded = collapsed && !expanded;
  const open = () => { if (folded) { setExpanded(true); return; } nav(`/post/${post.id}`); };

  return (
    <div
      role="link" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }}
      className="yalola-card p-4 space-y-2.5 cursor-pointer hover:bg-[color:var(--yalola-surface-alt)] transition-colors"
      style={{ borderColor: GREEN_LINE, backgroundImage: `radial-gradient(circle at 100% 0%, var(--yalola-secondary-soft) 0, transparent 120px)` }}
      data-testid="featured-testimony" data-post-id={post.id} data-folded={folded ? "1" : "0"}
    >
      <div className="flex items-center gap-2">
        <span className="h-8 w-8 rounded-full grid place-items-center shrink-0" style={{ background: GREEN_SOFT }}><Star size={15} color={GREEN} fill={GREEN} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: GREEN }}>{t("testimony.featured", "Témoignage de la semaine")}</div>
          <div className="text-sm font-semibold flex items-center gap-1.5 min-w-0">
            <Avatar className="h-5 w-5"><AvatarImage src={avatarSrc(post.author, 32)} /><AvatarFallback className="text-[9px]">{post.author?.name?.[0]}</AvatarFallback></Avatar>
            <span className="truncate">{post.author?.name}</span>
            <VerifiedBadge user={post.author} size={12} />
            <span className="chip text-[10px] px-1.5 py-0 shrink-0" style={{ background: GREEN_SOFT, color: GREEN, borderColor: "transparent" }} data-testid="featured-testimony-category">{t(cat[0], cat[1])}</span>
          </div>
        </div>
        <DismissX onClick={onDismiss} testId="featured-testimony-dismiss" label={t("smart_cards.dismiss", "Masquer")} />
      </div>
      <div className="flex items-start gap-3">
        <p className={`text-sm leading-snug flex-1 min-w-0 ${folded ? "line-clamp-1" : "whitespace-pre-wrap"}`} style={{ color: "var(--yalola-text)" }} data-testid="featured-testimony-excerpt">{text}</p>
        {image && !folded && (
          <img
            src={imageVariant(image, 72)} alt="" loading="lazy"
            className="h-[72px] w-[72px] rounded-lg object-cover shrink-0"
            style={{ background: "var(--yalola-surface-alt)" }}
            data-testid="featured-testimony-image"
          />
        )}
      </div>
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold" style={{ color: GREEN }} data-testid="featured-testimony-link">
        {folded ? t("testimony.read_more", "Lire") : t("testimony.read_full", "Lire le témoignage")} <ChevronRight size={14} />
      </span>
    </div>
  );
};

export default FeaturedTestimony;
